/**
 * 字幕分段算法模块
 *
 * 将字幕文本按换行符分割为带时间信息的分段列表，
 * 前端（PreviewTab、TextTrackItem）和后端（VideoExportService）共用同一实现，
 * 从根本上保证预览与导出的一致性。
 */

/**
 * 字幕分段结果
 */
export interface SubtitleSegment {
  /** 分段文本 */
  text: string;
  /** 相对于 clip 起始的开始偏移（秒） */
  startOffset: number;
  /** 相对于 clip 起始的结束偏移（秒） */
  endOffset: number;
}

export interface SubtitleSegmentRange extends SubtitleSegment {
  textStart: number;
  textEnd: number;
}

export interface ShortDramaSubtitleFormatOptions {
  maxCjkCharsPerSegment?: number;
  maxLatinCharsPerSegment?: number;
  preferredSegments?: number;
}

/**
 * 中英文标点符号正则，用于计算有效字数时过滤
 */
const PUNCTUATION_REGEX =
  /[\u3000-\u303F\uFF00-\uFFEF\u2000-\u206F\u0021-\u002F\u003A-\u0040\u005B-\u0060\u007B-\u007E\u00A0-\u00BF\u2010-\u2027\u2030-\u205E\u2060-\u2FFF\u3001-\u301F\uFE30-\uFE4F\uFE50-\uFE6F\uFF01-\uFF0F\uFF1A-\uFF20\uFF3B-\uFF40\uFF5B-\uFF65\u3002\u300C\u300D\u300E\u300F\u3010\u3011\u3014\u3015\u3016\u3017\u3018\u3019\u301A\u301B\u201C\u201D\u2018\u2019\u2026\u2014\u2013\u00B7]/g;

/**
 * 计算文本的有效字数（过滤中英文标点后的字符数，最小值为 1 防止除零）
 */
function getEffectiveCharCount(text: string): number {
  const filtered = text.replace(PUNCTUATION_REGEX, '').replace(/\s+/g, '');
  return Math.max(filtered.length, 1);
}

function countMatches(text: string, regex: RegExp): number {
  return (text.match(regex) || []).length;
}

function resolveMaxEffectiveChars(
  text: string,
  options?: ShortDramaSubtitleFormatOptions,
): number {
  const cjkCount = countMatches(text, /[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/g);
  const latinCount = countMatches(text, /[A-Za-z]/g);
  const preferredSegments = Math.max(1, options?.preferredSegments ?? 1);

  if (cjkCount >= latinCount) {
    const base = options?.maxCjkCharsPerSegment ?? 14;
    return Math.ceil(base * preferredSegments);
  }

  const base = options?.maxLatinCharsPerSegment ?? 24;
  return Math.ceil(base * preferredSegments);
}

function stripTrailingSubtitlePunctuation(text: string): string {
  return text.replace(/[\s\u00A0]+$/g, '').replace(/[.,，。]+$/u, '').trim();
}

export function stripTerminalSubtitlePunctuation(script: string): string {
  if (!script?.trim()) {
    return '';
  }

  return script
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => stripTrailingSubtitlePunctuation(line))
    .filter((line) => line.length > 0)
    .join('\n');
}

function findSplitIndex(rawText: string): number {
  const text = rawText.trim();
  if (!text) return -1;

  const midpoint = Math.floor(text.length / 2);
  const candidates: number[] = [];
  const punctuationRegex = /[，,。.!?！？；;：:]/gu;
  let punctuationMatch: RegExpExecArray | null;

  while ((punctuationMatch = punctuationRegex.exec(text)) !== null) {
    const nextIndex = punctuationMatch.index + punctuationMatch[0].length;
    if (nextIndex > 0 && nextIndex < text.length) {
      candidates.push(nextIndex);
    }
  }

  const englishBreakRegex = /\s+(and|but|or|so|because|while|when|if|then)\s+/gi;
  let englishBreakMatch: RegExpExecArray | null;
  while ((englishBreakMatch = englishBreakRegex.exec(text)) !== null) {
    const nextIndex = englishBreakMatch.index + englishBreakMatch[0].length - englishBreakMatch[1].length - 1;
    if (nextIndex > 0 && nextIndex < text.length) {
      candidates.push(nextIndex);
    }
  }

  const chineseBreakRegex = /(然后|但是|不过|所以|于是|如果|并且|而且|却)/gu;
  let chineseBreakMatch: RegExpExecArray | null;
  while ((chineseBreakMatch = chineseBreakRegex.exec(text)) !== null) {
    const nextIndex = chineseBreakMatch.index;
    if (nextIndex > 0 && nextIndex < text.length) {
      candidates.push(nextIndex);
    }
  }

  if (candidates.length > 0) {
    return candidates.sort((a, b) => Math.abs(a - midpoint) - Math.abs(b - midpoint))[0];
  }

  const whitespaceCandidates: number[] = [];
  const whitespaceRegex = /\s+/g;
  let whitespaceMatch: RegExpExecArray | null;
  while ((whitespaceMatch = whitespaceRegex.exec(text)) !== null) {
    const nextIndex = whitespaceMatch.index + whitespaceMatch[0].length;
    if (nextIndex > 0 && nextIndex < text.length) {
      whitespaceCandidates.push(nextIndex);
    }
  }

  if (whitespaceCandidates.length > 0) {
    return whitespaceCandidates.sort((a, b) => Math.abs(a - midpoint) - Math.abs(b - midpoint))[0];
  }

  return midpoint > 0 && midpoint < text.length ? midpoint : -1;
}

function splitLongSubtitleSegment(
  text: string,
  maxEffectiveChars: number,
): string[] {
  const normalized = text.trim();
  if (!normalized) return [];

  if (getEffectiveCharCount(normalized) <= maxEffectiveChars) {
    return [stripTrailingSubtitlePunctuation(normalized)];
  }

  const splitIndex = findSplitIndex(normalized);
  if (splitIndex <= 0 || splitIndex >= normalized.length) {
    return [stripTrailingSubtitlePunctuation(normalized)];
  }

  const left = normalized.slice(0, splitIndex).trim();
  const right = normalized.slice(splitIndex).trim();

  if (!left || !right) {
    return [stripTrailingSubtitlePunctuation(normalized)];
  }

  return [
    ...splitLongSubtitleSegment(left, maxEffectiveChars),
    ...splitLongSubtitleSegment(right, maxEffectiveChars),
  ];
}

export function formatShortDramaSubtitle(
  script: string,
  options?: ShortDramaSubtitleFormatOptions,
): string {
  if (!script?.trim()) {
    return '';
  }

  const normalizedScript = script.replace(/\r\n/g, '\n').trim();
  const paragraphs = normalizedScript
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (paragraphs.length === 0) {
    return '';
  }

  const formattedSegments = paragraphs.flatMap((paragraph) =>
    splitLongSubtitleSegment(
      paragraph,
      resolveMaxEffectiveChars(paragraph, options),
    ),
  );

  return formattedSegments
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .join('\n');
}

/**
 * 将字幕文本按换行符分割为带时间信息的分段列表
 *
 * @param script      字幕全文（可含 \n 换行符）
 * @param duration    clip 总时长（秒）
 * @param minDuration 每段最小显示时长（秒），默认 1.0
 * @returns           分段列表，总时长严格等于 duration
 */
export function splitSubtitleSegments(
  script: string,
  duration: number,
  minDuration = 1.0,
): SubtitleSegment[] {
  // 边界：script 为空或 duration <= 0 时返回空数组
  if (!script || duration <= 0) {
    return [];
  }

  // 按 \n 分割并过滤空行
  const lines = script.split('\n').filter((line) => line.trim().length > 0);

  // 所有行过滤后均为空时，返回单段（原始 script，全程显示）
  if (lines.length === 0) {
    return [{ text: script, startOffset: 0, endOffset: duration }];
  }

  // 单行（无换行符或只有一行有效内容）直接返回单段
  if (lines.length === 1) {
    return [{ text: lines[0], startOffset: 0, endOffset: duration }];
  }

  // 计算各行有效字数
  const effectiveCounts = lines.map(getEffectiveCharCount);
  const totalEffective = effectiveCounts.reduce((sum, c) => sum + c, 0);

  let durations: number[];

  if (lines.length * minDuration <= duration) {
    // 优先路径：按字数比例分配，再将低于 minDuration 的段提升至 minDuration，最后归一化
    const proportional = effectiveCounts.map(
      (count) => (count / totalEffective) * duration,
    );

    // 将低于 minDuration 的段提升至 minDuration
    const raised = proportional.map((d) => Math.max(d, minDuration));

    // 归一化使总和严格等于 duration
    const raisedTotal = raised.reduce((sum, d) => sum + d, 0);
    durations = raised.map((d) => (d / raisedTotal) * duration);
  } else {
    // 降级路径：直接按字数比例分配，不保障最小时长
    durations = effectiveCounts.map(
      (count) => (count / totalEffective) * duration,
    );
  }

  // 构建分段列表
  const segments: SubtitleSegment[] = [];
  let currentOffset = 0;

  for (let i = 0; i < lines.length; i++) {
    const startOffset = currentOffset;
    let endOffset: number;

    if (i === lines.length - 1) {
      // 最后一段直接赋值为 duration，消除浮点累积误差
      endOffset = duration;
    } else {
      endOffset = currentOffset + durations[i];
    }

    segments.push({ text: lines[i], startOffset, endOffset });
    currentOffset = endOffset;
  }

  return segments;
}

/**
 * 根据当前播放时间（localTime）从分段列表中找到应显示的分段
 *
 * @param segments  splitSubtitleSegments() 的返回值
 * @param localTime 相对于 clip 起始的当前时间（秒）
 * @returns         当前应显示的分段；空数组返回 null
 */
export function getCurrentSegment(
  segments: SubtitleSegment[],
  localTime: number,
): SubtitleSegment | null {
  if (segments.length === 0) {
    return null;
  }

  // localTime < 0 返回第一段
  if (localTime < 0) {
    return segments[0];
  }

  // localTime >= 最后一段的 endOffset 返回最后一段
  const last = segments[segments.length - 1];
  if (localTime >= last.endOffset) {
    return last;
  }

  // 线性扫描找到满足 startOffset <= localTime < endOffset 的分段
  for (const segment of segments) {
    if (localTime >= segment.startOffset && localTime < segment.endOffset) {
      return segment;
    }
  }

  // 兜底：返回最后一段
  return last;
}

export function mapSubtitleSegmentsToTextRanges(
  script: string,
  segments: SubtitleSegment[],
): SubtitleSegmentRange[] {
  if (!script || segments.length === 0) return [];

  let searchCursor = 0;

  return segments.map((segment) => {
    const foundIndex = script.indexOf(segment.text, searchCursor);
    const textStart = foundIndex >= 0 ? foundIndex : script.indexOf(segment.text);
    const safeTextStart = Math.max(0, textStart);
    const textEnd = safeTextStart + segment.text.length;
    searchCursor = textEnd;

    return {
      ...segment,
      textStart: safeTextStart,
      textEnd,
    };
  });
}
